import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { BarChart3, Users, TrendingUp, Gift, Package, AlertTriangle, Calendar, Activity, Star, Award } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays, startOfDay, startOfWeek, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Analytics() {
  const [user, setUser] = useState(null);
  const [timeRange, setTimeRange] = useState('week'); // day, week, month

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
        
        if (currentUser.role !== 'admin') {
          window.location.href = '/';
        }
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  // Fetch all data
  const { data: allUsers = [] } = useQuery({
    queryKey: ['all-users'],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['all-products'],
    queryFn: () => base44.entities.Product.list('-created_date'),
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['all-activities'],
    queryFn: () => base44.entities.ProductActivity.list('-created_date'),
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ['all-transactions'],
    queryFn: () => base44.entities.PointTransaction.list('-created_date'),
  });

  const { data: userRewards = [] } = useQuery({
    queryKey: ['all-user-rewards'],
    queryFn: () => base44.entities.UserReward.list('-redeemed_at'),
  });

  const { data: rewards = [] } = useQuery({
    queryKey: ['all-rewards'],
    queryFn: () => base44.entities.Reward.list(),
  });

  const { data: posts = [] } = useQuery({
    queryKey: ['all-posts'],
    queryFn: () => base44.entities.Post.list('-created_date'),
  });

  const { data: recipeFavorites = [] } = useQuery({
    queryKey: ['all-recipe-favorites'],
    queryFn: () => base44.entities.RecipeFavorite.list(),
  });

  const { data: additives = [] } = useQuery({
    queryKey: ['all-additives'],
    queryFn: () => base44.entities.Additive.list(),
  });

  // Calculate active users
  const getActiveUsers = (range) => {
    const now = new Date();
    let startDate;
    
    if (range === 'day') {
      startDate = startOfDay(now);
    } else if (range === 'week') {
      startDate = startOfWeek(now);
    } else {
      startDate = startOfMonth(now);
    }

    const activeUserIds = new Set();
    
    activities.forEach(activity => {
      if (new Date(activity.created_date) >= startDate) {
        activeUserIds.add(activity.user_id);
      }
    });
    
    transactions.forEach(transaction => {
      if (new Date(transaction.created_date) >= startDate) {
        activeUserIds.add(transaction.user_id);
      }
    });

    return activeUserIds.size;
  };

  // Daily active users for the last 7 days
  const getDailyActiveUsers = () => {
    const data = [];
    for (let i = 6; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const startDate = startOfDay(date);
      const endDate = startOfDay(subDays(new Date(), i - 1));
      
      const activeUserIds = new Set();
      
      activities.forEach(activity => {
        const activityDate = new Date(activity.created_date);
        if (activityDate >= startDate && activityDate < endDate) {
          activeUserIds.add(activity.user_id);
        }
      });

      data.push({
        date: format(date, 'd MMM', { locale: es }),
        users: activeUserIds.size
      });
    }
    return data;
  };

  // Feature adoption rates
  const getFeatureAdoption = () => {
    const totalUsers = allUsers.length;
    
    const usersWithScans = new Set(activities.filter(a => a.activity_type === 'scan').map(a => a.user_id)).size;
    const usersWithReviews = new Set(activities.filter(a => a.activity_type === 'review').map(a => a.user_id)).size;
    const usersWithPosts = new Set(posts.map(p => p.created_by)).size;
    const usersWithRecipeFavorites = new Set(recipeFavorites.map(r => r.user_id)).size;

    return [
      { feature: 'Escaneo', users: usersWithScans, rate: totalUsers > 0 ? ((usersWithScans / totalUsers) * 100).toFixed(1) : 0 },
      { feature: 'Reseñas', users: usersWithReviews, rate: totalUsers > 0 ? ((usersWithReviews / totalUsers) * 100).toFixed(1) : 0 },
      { feature: 'Comunidad', users: usersWithPosts, rate: totalUsers > 0 ? ((usersWithPosts / totalUsers) * 100).toFixed(1) : 0 },
      { feature: 'Recetas', users: usersWithRecipeFavorites, rate: totalUsers > 0 ? ((usersWithRecipeFavorites / totalUsers) * 100).toFixed(1) : 0 },
    ];
  };

  // Most redeemed rewards
  const getMostRedeemedRewards = () => {
    const rewardCounts = {};
    
    userRewards.forEach(ur => {
      rewardCounts[ur.reward_id] = (rewardCounts[ur.reward_id] || 0) + 1;
    });

    return Object.entries(rewardCounts)
      .map(([rewardId, count]) => {
        const reward = rewards.find(r => r.id === rewardId);
        return {
          name: reward?.name || 'Desconocido',
          count,
          icon: reward?.icon || '🎁'
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  // Points activity
  const getPointsActivity = () => {
    const totalPointsAwarded = transactions.filter(t => t.points > 0).reduce((sum, t) => sum + t.points, 0);
    const totalPointsRedeemed = Math.abs(transactions.filter(t => t.points < 0).reduce((sum, t) => sum + t.points, 0));
    
    const actionCounts = {};
    transactions.forEach(t => {
      if (t.points > 0) {
        actionCounts[t.action] = (actionCounts[t.action] || 0) + 1;
      }
    });

    const topActions = Object.entries(actionCounts)
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return { totalPointsAwarded, totalPointsRedeemed, topActions };
  };

  // Most scanned products
  const getMostScannedProducts = () => {
    const productCounts = {};
    
    activities.filter(a => a.activity_type === 'scan').forEach(activity => {
      productCounts[activity.product_id] = (productCounts[activity.product_id] || 0) + 1;
    });

    return Object.entries(productCounts)
      .map(([productId, count]) => {
        const product = products.find(p => p.id === productId);
        return {
          name: product?.name || 'Desconocido',
          brand: product?.brand || '',
          count,
          score: product?.scaneat_score
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  // Product categories distribution
  const getCategoryDistribution = () => {
    const categoryCounts = {};
    
    products.forEach(product => {
      const category = product.category || 'Sin categoría';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    return Object.entries(categoryCounts)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  };

  // Most searched additives
  const getMostFoundAdditives = () => {
    return additives
      .map(additive => ({
        code: additive.code,
        name: additive.name,
        risk_level: additive.risk_level,
        count: additive.product_ids?.length || 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const dailyActiveUsers = getDailyActiveUsers();
  const featureAdoption = getFeatureAdoption();
  const mostRedeemedRewards = getMostRedeemedRewards();
  const pointsActivity = getPointsActivity();
  const mostScannedProducts = getMostScannedProducts();
  const categoryDistribution = getCategoryDistribution();
  const mostFoundAdditives = getMostFoundAdditives();

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#84cc16'];

  const getRiskColor = (level) => {
    switch(level) {
      case 'bajo': return '#10b981';
      case 'moderado': return '#f59e0b';
      case 'alto': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-gray-50 pb-20">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-lg">Dashboard de Analíticas</h1>
              <p className="text-xs text-gray-400">Métricas y estadísticas del sistema</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="overview">Resumen</TabsTrigger>
            <TabsTrigger value="users">Usuarios</TabsTrigger>
            <TabsTrigger value="products">Productos</TabsTrigger>
            <TabsTrigger value="loyalty">Lealtad</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8 text-blue-600" />
                  <span className="text-xs text-gray-500">Total</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{allUsers.length}</p>
                <p className="text-sm text-gray-600">Usuarios registrados</p>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Activity className="w-8 h-8 text-green-600" />
                  <span className="text-xs text-gray-500">Activos</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{getActiveUsers(timeRange)}</p>
                <p className="text-sm text-gray-600">
                  {timeRange === 'day' ? 'Hoy' : timeRange === 'week' ? 'Esta semana' : 'Este mes'}
                </p>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Package className="w-8 h-8 text-purple-600" />
                  <span className="text-xs text-gray-500">Escaneos</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{products.length}</p>
                <p className="text-sm text-gray-600">Productos analizados</p>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Gift className="w-8 h-8 text-amber-600" />
                  <span className="text-xs text-gray-500">Canjes</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{userRewards.length}</p>
                <p className="text-sm text-gray-600">Recompensas canjeadas</p>
              </Card>
            </div>

            {/* Daily Active Users Chart */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Usuarios Activos - Últimos 7 días</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyActiveUsers}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="users" stroke="#8b5cf6" name="Usuarios" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            {/* Feature Adoption */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Tasa de Adopción de Funcionalidades</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={featureAdoption}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="feature" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="rate" fill="#3b82f6" name="% Adopción" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Feature Adoption Details */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Adopción por Funcionalidad
                </h3>
                <div className="space-y-4">
                  {featureAdoption.map((feature, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{feature.feature}</span>
                        <span className="text-sm font-bold text-blue-600">{feature.rate}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${feature.rate}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{feature.users} usuarios</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Daily Active Users Table */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-600" />
                  Usuarios Activos por Período
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Hoy</span>
                    <span className="text-2xl font-bold text-green-700">{getActiveUsers('day')}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Esta Semana</span>
                    <span className="text-2xl font-bold text-blue-700">{getActiveUsers('week')}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Este Mes</span>
                    <span className="text-2xl font-bold text-purple-700">{getActiveUsers('month')}</span>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Most Scanned Products */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-600" />
                  Productos Más Escaneados
                </h3>
                <div className="space-y-3">
                  {mostScannedProducts.map((product, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                        {product.brand && (
                          <p className="text-xs text-gray-500">{product.brand}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{product.count}</p>
                        <p className="text-xs text-gray-500">escaneos</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Category Distribution */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Distribución por Categoría</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {categoryDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>

              {/* Most Found Additives */}
              <Card className="p-6 md:col-span-2">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Aditivos Más Encontrados
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {mostFoundAdditives.map((additive, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-xs"
                        style={{ backgroundColor: `${getRiskColor(additive.risk_level)}20`, color: getRiskColor(additive.risk_level) }}
                      >
                        {additive.code}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{additive.name}</p>
                        <p className="text-xs text-gray-500 capitalize">Riesgo {additive.risk_level}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{additive.count}</p>
                        <p className="text-xs text-gray-500">productos</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Loyalty Tab */}
          <TabsContent value="loyalty" className="space-y-6">
            {/* Points Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-8 h-8 opacity-80" />
                </div>
                <p className="text-3xl font-bold">{pointsActivity.totalPointsAwarded.toLocaleString()}</p>
                <p className="text-sm opacity-90">Puntos Otorgados</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-red-500 to-pink-600 text-white">
                <div className="flex items-center justify-between mb-2">
                  <Gift className="w-8 h-8 opacity-80" />
                </div>
                <p className="text-3xl font-bold">{pointsActivity.totalPointsRedeemed.toLocaleString()}</p>
                <p className="text-sm opacity-90">Puntos Canjeados</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                <div className="flex items-center justify-between mb-2">
                  <Award className="w-8 h-8 opacity-80" />
                </div>
                <p className="text-3xl font-bold">{transactions.length}</p>
                <p className="text-sm opacity-90">Transacciones Totales</p>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Most Redeemed Rewards */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-amber-600" />
                  Recompensas Más Canjeadas
                </h3>
                <div className="space-y-3">
                  {mostRedeemedRewards.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No hay canjes aún</p>
                  ) : (
                    mostRedeemedRewards.map((reward, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                        <span className="text-3xl">{reward.icon}</span>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{reward.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-amber-700 text-lg">{reward.count}</p>
                          <p className="text-xs text-gray-500">canjes</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              {/* Top Point Actions */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-blue-600" />
                  Actividades Más Frecuentes
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={pointsActivity.topActions}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="action" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}